import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment.test';
import { AppComponent } from '../../../app.component';
import { HasUnsavedChanges } from '../../../route-guards/unsaved-changes.route-guard';
import { CurrentUserService } from '../../../security/current-user.service';
import { IFormBuilderAssistant } from '../../../shared/components/form-builder/form-builder-assistant/iform-builder-assistant.interface';
import { FormTemplateDesign_FormBuilderAssistant } from './form-builder-assistant';
import { FormBuilderComponent } from '../../../shared/components/form-builder/form-builder.component';
import { AlertDialogComponent, AlertDialogModel } from '../../../shared/dialogs/alert/alert-dialog.component';
import { FormErrorMessages } from '../../../shared/models/error-messages/form-error-messages';
import { FieldDefinition } from '../../../shared/models/form-builder/field-definition.model';
import { Form } from '../../../shared/models/form-builder/form.model';
import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { DataCollectionService } from '../../../shared/services/data-collection.service';
import { FieldDefinitionService } from '../../../shared/services/field-definition.service';
import { FormBuilderService } from '../../../shared/services/form-builder.service';
import { CachedFormFieldService } from '../../../shared/services/form-field-with-cache.service';
//import { FormTemplateHtmlFieldsDesign_FormBuilderAssistant } from '../design-form-html/form-builder-assistant';

@Component({
    selector: 'app-design',
    templateUrl: './design.component.html',
    styleUrls: ['./design.component.scss'],
    standalone: false
})
export class DesignComponent implements OnInit, HasUnsavedChanges {
    // Instance data.
    private fieldDefinitions: FieldDefinition[];
    protected form: Form;
    private site: DataCollection;
    private returnPath: string;

    @ViewChild('formBuilder') formBuilder: FormBuilderComponent;

    private transientFormBuilderComponent: FormBuilderComponent = null;
    protected formBuilderAssistant: IFormBuilderAssistant = new FormTemplateDesign_FormBuilderAssistant();
    //private formBuilderAssistant: IFormBuilderAssistant = new FormTemplateHtmlFieldsDesign_FormBuilderAssistant();

    // Constructor.
    public constructor(
        protected route: ActivatedRoute,
        private router: Router,
        protected formBuilderService: FormBuilderService,
        private dataCollectionService: DataCollectionService,
        private fieldDefinitionService: FieldDefinitionService,
        private cachedFormFieldService: CachedFormFieldService,
        private currentUserService: CurrentUserService,
        private readonly titleService: Title,
        public dialog: MatDialog,
        private appComponent: AppComponent //VNEXT-235: KLW - Inject AppComponent    
    ) {
        this.cachedFormFieldService.clearCache();
    }

    // Life cycle methods.
    public ngOnInit() {
        this.returnPath = history.state.returnPath;
        this.route.parent.data
            .subscribe((data: { currentSite: DataCollection }) => {
                this.site = data.currentSite;

                this.route.data
                    .subscribe((data: { form: Form }) => {
                        this.form = data.form;

                        if (this.form.id == 0) {
                            this.form.dataCollectionId = this.site.id;
                        }
                    });
            });

        this.fieldDefinitionService.getAll().then(response => {
            this.fieldDefinitions = response;
        });
        this.titleService.setTitle(this.route.snapshot.data['title'] + ' ' + this.form.name);

        // For simulating the HTML-edit-only mode:
        //this.formBuilderAssistant = new FormTemplateHtmlFieldsDesign_FormBuilderAssistant();
    }

    // Begin methods called by my HTML code.
    public get FieldDefinitions(): FieldDefinition[] {
        return this.fieldDefinitions;
    }
    public get Form(): Form {
        return this.form;
    }
    public get FormBuilderAssistant(): IFormBuilderAssistant {
        return this.formBuilderAssistant;
    }
    // End methods called by my HTML code.

    // Implement the HasUnsavedChanges interface and protect against an uload with unsaved changes
    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload($event: any) {
        if (this.currentUserService.userIsLoggedIn && this.hasUnsavedChanges()) {
            $event.returnValue = "You have unsaved changes!";
        }
    }
    public hasUnsavedChanges(): boolean {
        if (environment.suppressUnsavedChangesWarning) {
            return false;
        }
        return this.formBuilder.hasUnsavedChanges();
    }

    // Implement interface ICanSave.
    public saveForm(formBuilderParam: FormBuilderComponent) {
        this.transientFormBuilderComponent = formBuilderParam;
        let e: Form = this.transientFormBuilderComponent.Form;

        // Does a form with this name already exist?
        this.dataCollectionService.getFormsBySite(this.site.id).then(forms => {
            let existingForm: Form = forms.find(f => (f.name == e.name) && (f.id != e.id));

            if (existingForm == null) {
                this.doSaveForm(e);
            } else {
                this.transientFormBuilderComponent.saveCouldNotBePerformed();

                let message: string = FormErrorMessages.duplicateFormInSiteMessage(e.name, this.site.id); //`Cannot save this form definition as '${e.name}' as a form definition with that name already exists in the current site.`;
                this.dialog.open(AlertDialogComponent, {
                    maxWidth: "600px",
                    data: new AlertDialogModel("Cannot Save Form", message)
                });
            }
        });
    }

    // Helper methods.
    private doSaveForm(e: Form): void {
        // Save the form.
        this.formBuilderService.save(e).then((response: Form) => {
            // Save the updated form.
            this.form = response;

            //VNEXT-235: KLW - Form the URL that will direct to the newly created form instance to be routed on a page refresh
            let savedFormInstanceURL = `/site/${this.site.id}/design/${this.form.id}`;
            this.appComponent.setFormInstanceURLRefresh(savedFormInstanceURL);

            this.transientFormBuilderComponent.saveCompleted(this.form);
            this.transientFormBuilderComponent = null;

            if (this.returnPath != null) this.router.navigateByUrl(this.returnPath);
        }).catch((reason: any) => {
            //VNEXT-413: KLW - When there is an error while performing a save, be sure to re-enable the appropiate HTML objects
            this.transientFormBuilderComponent.saveCouldNotBePerformed();

            console.log('error');
            console.log(reason);
        });
    }
}
